import { sortBy } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { SearchSelect, type FormControlSizes, type SearchSelectProps } from '../components/Form';
import { EMPTY_ARRAY } from '../utils';
import type { SelectorField } from './SelectorField';

export interface SelectorFieldViewProps<T> {
  field: SelectorField<T>;
  handleChange: (value: T | undefined) => void;
  labelGetter: (value: T) => string;
  descriptionGetter?: (value: T) => string;
  suffix?: ReactNode;
  prefix?: ReactNode;
  testid?: string;
  showClear?: boolean;
  initialSortByLabel?: boolean;
  initialSortGroupByLabel?: boolean;
  size?: FormControlSizes;
  groupField?: keyof T | ((item: T) => string);
  id?: string;
  portalize?: boolean;

  dropdownWidth?: SearchSelectProps<T>['dropdownWidth'];
  dropdownPlacement?: SearchSelectProps<T>['dropdownPlacement'];
}
export function SelectorFieldView<T>({
  field,
  handleChange,
  labelGetter,
  descriptionGetter,
  suffix,
  prefix,
  testid,
  showClear,
  initialSortByLabel,
  size,
  groupField,
  initialSortGroupByLabel,
  id,
  portalize = false,
  dropdownPlacement,
  dropdownWidth,
}: SelectorFieldViewProps<T>) {
  const availableItems = useMemo(() => {
    if (!groupField) {
      return field?.availableItems ?? EMPTY_ARRAY;
    }
    const grouped = field?.availableItems.reduce((acc, item) => {
      const group = (typeof groupField === 'function' ? groupField(item) : item[groupField]) || '';
      const groupItems = acc.get(group) || [];
      return acc.set(group, [...groupItems, item]);
    }, new Map());

    const entries: [string, any][] = Array.from(grouped?.entries() ?? []);
    return (initialSortGroupByLabel ? sortBy(entries, '0') : entries).map(([group, items]) => ({ group, items }));
  }, [field?.availableItems, groupField, initialSortGroupByLabel]);

  return (
    <SearchSelect
      w="100%"
      id={id}
      selection={field?.value}
      placeholder={field?.placeholder || ''}
      invalid={field?.hasInvalidValue}
      touched={field?.isTouched}
      options={availableItems}
      getLabel={labelGetter}
      getDescription={descriptionGetter}
      disabled={field?.isDisabled}
      onChange={handleChange}
      suffix={suffix}
      prefix={prefix}
      showClear={showClear}
      data-testid={testid}
      initialSortByLabel={initialSortByLabel}
      size={size}
      portalize={portalize}
      dropdownWidth={dropdownWidth}
      dropdownPlacement={dropdownPlacement}
    />
  );
}
